import { campaignSteps } from '@/components/CreateCampaignDialog/campaignSteps'
import CloseIcon from '@/components/Icons/CloseIcon'
import { FormActions } from '@/components/MultiStepForm/FormActions/FormActions'
import { StepIndicator } from '@/components/MultiStepForm/StepIndicator/StepIndicator'
import { useMultiStepForm } from '@/hooks/useMultiStepForm'
import {
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useUploadCampaignAssetsMutation,
  useUploadCampaignCoverImageMutation,
} from '@/services/campaigns/mutations'
import type { UpdateCampaignPayload } from '@/services/campaigns/types'
import commonStyles from '@/styles/common.module.css'
import { Dialog } from 'radix-ui'
import { handleNextStep } from './handleNextStep'
import styles from './styles.module.css'
import { validateStep5 } from './validations'

const CreateCampaignDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const { currentStep, formData, errors, nextStep, prevStep, updateData, updateErrors, resetForm } = useMultiStepForm(campaignSteps.length)
  const CurrentStepComponent = campaignSteps[currentStep - 1].component

  const { mutateAsync: createCampaign, isPending: isCreatingCampaign } = useCreateCampaignMutation()
  const { mutateAsync: uploadCoverImage, isPending: isUploadingImage } = useUploadCampaignCoverImageMutation()
  const { mutateAsync: uploadAssets, isPending: isUploadingAssets } = useUploadCampaignAssetsMutation()
  const { mutateAsync: updateCampaign, isPending: isUpdatingCampaign } = useUpdateCampaignMutation()

  const onNext = () =>
    handleNextStep({
      currentStep,
      formData,
      updateErrors,
      updateData,
      nextStep,
      createCampaign,
      uploadCoverImage,
      uploadAssets,
    })

  const isLoading = isCreatingCampaign || isUploadingImage || isUploadingAssets || isUpdatingCampaign

  const handleSubmit = async () => {
    const validationResult = validateStep5(formData)
    if (!validationResult.isValid) {
      updateErrors(validationResult.errors)
      return
    }
    updateErrors({})

    const payoutEnabled = Array.isArray(formData.rewards) && formData.rewards.includes('Payout')

    if (payoutEnabled && !formData.budget) {
      throw new Error('Budget missing after validation')
    }

    const updateObject: UpdateCampaignPayload = {
      id: Number(formData.id),
      rewards: formData.rewards ?? [],
    }

    if (payoutEnabled && formData.budget) {
      updateObject.budget = formData.budget
    }

    if (payoutEnabled && typeof formData.payout === 'number' && Number.isFinite(formData.payout)) {
      updateObject.payout = formData.payout
    }

    await updateCampaign(updateObject)
    onOpenChange(false)
    resetForm()
  }

  const handleCancel = () => {
    onOpenChange(false)
    resetForm()
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.Overlay} />
        <Dialog.Content className={`${styles.Content} ${currentStep === 2 ? styles.ContentGeneralInfo : ''}`}>
          <Dialog.Title className={styles.Title}>Create Campaign</Dialog.Title>
          <Dialog.Description className={styles.Description}>
            Set up a new marketing campaign. Fill in the details below to get started.
          </Dialog.Description>

          <StepIndicator steps={campaignSteps} currentStep={currentStep} />

          {isUploadingAssets || isUploadingImage ? (
            <div className={commonStyles.loadingContainerCenter}>
              <div className={commonStyles.loadingSpinner} />
              <p className={commonStyles.loadingText}>Uploading {isUploadingAssets ? 'assets' : 'image'}...</p>
            </div>
          ) : (
            <div className={styles.FormContainer}>
              <CurrentStepComponent data={formData} updateData={updateData} errors={errors} />
            </div>
          )}

          <FormActions
            currentStep={currentStep}
            totalSteps={campaignSteps.length}
            onBack={prevStep}
            onNext={onNext}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            isSubmitting={isLoading}
          />

          <Dialog.Close asChild>
            <button type="button" className={styles.IconButton} aria-label="Close">
              <CloseIcon size={20} />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
export default CreateCampaignDialog
